"use client"

import { createContext, useContext, useLayoutEffect, useState } from "react"
import type { ReactNode } from "react"

import { FlowPanel } from "@/components/flow/FlowPanel"
import { OnboardingOptionsContext } from "@/features/checkout/redesign/OnboardingPhases"
import { ClientExperimentTracker } from "@/platform/experiments/ClientExperimentTracker"
import type { OnboardingFlowAssignment } from "@/platform/experiments/onboarding-flow"
import { readCheckoutOverride } from "@/platform/experiments/read-checkout-override"
import type { TreatmentConfig } from "@/platform/experiments/types"
import { setOnboardingFlow } from "@/platform/observability/analytics/v2/onboarding-flow"

export interface CheckoutConfig {
  allowDlAndPassport: TreatmentConfig<boolean>
  skipOnfidoSelfieValidation: TreatmentConfig<boolean>
  verificationPathExperiment: TreatmentConfig<string>
  showDeliveryNotesField: TreatmentConfig<boolean>
  onboarding: OnboardingFlowAssignment
}

const MARKETPLACE_ONBOARDING = { showInfo: true, showProgress: true, verifyAfterOrder: true }

const CheckoutConfigContext = createContext<CheckoutConfig | null>(null)

export const CheckoutConfigProvider = ({ children, ...raw }: CheckoutConfig & { children: ReactNode }) => {
  const [config] = useState(() => ({
    allowDlAndPassport: readCheckoutOverride("allowDlAndPassport", raw.allowDlAndPassport),
    skipOnfidoSelfieValidation: readCheckoutOverride("skipOnfidoSelfieValidation", raw.skipOnfidoSelfieValidation),
    verificationPathExperiment: readCheckoutOverride("verificationPathExperiment", raw.verificationPathExperiment),
    showDeliveryNotesField: readCheckoutOverride("showDeliveryNotesField", raw.showDeliveryNotesField),
    onboarding: readCheckoutOverride("onboardingFlow", raw.onboarding),
  }))

  return <CheckoutConfigContext.Provider value={config}>{children}</CheckoutConfigContext.Provider>
}

export const useCheckoutConfig = (): CheckoutConfig => {
  const ctx = useContext(CheckoutConfigContext)
  if (!ctx) throw new Error("useCheckoutConfig must be used within CheckoutConfigProvider")
  return ctx
}

export const useOnboardingRedesign = () => useCheckoutConfig().onboarding.flow !== "classic"

export const MarketplaceOnboarding = ({ children, panel = false }: { children: ReactNode; panel?: boolean }) => {
  const { onboarding } = useCheckoutConfig()
  const redesign = onboarding.flow !== "classic"

  useLayoutEffect(() => {
    setOnboardingFlow(onboarding.exposure ? onboarding.flow : null)
    return () => setOnboardingFlow(null)
  }, [onboarding])

  return (
    <OnboardingOptionsContext.Provider value={MARKETPLACE_ONBOARDING}>
      {onboarding.exposure && <ClientExperimentTracker experiment={onboarding.exposure} />}
      {!panel ? children : redesign ? <main className="flex min-h-dvh flex-col">{children}</main> : <FlowPanel>{children}</FlowPanel>}
    </OnboardingOptionsContext.Provider>
  )
}
